/**
 * SSR-safe, swallow-on-error wrappers around `globalThis.localStorage` /
 * `sessionStorage` for non-React call sites (slices, axios interceptors,
 * helper modules) that can't go through `useLocalStorage`.
 *
 * Why this exists:
 *   - We have an ESLint rule that bans direct `localStorage.*` access
 *     outside the storage discipline. This module is the canonical
 *     escape hatch for everything that isn't a React component.
 *   - Always SSR-guarded: `typeof window !== "undefined"`.
 *   - Quota / privacy-mode failures are swallowed so a single write
 *     can't crash a user flow.
 *   - Optional JSON serialization for the `*Json` variants.
 *
 * For React components, prefer `useLocalStorage` — it gives you a
 * reactive `[value, setValue, remove]` triple with the same guarantees.
 */

import { logger } from "@/shared/lib/logger";

const isBrowser = (): boolean => typeof window !== "undefined";

// ─── String values ────────────────────────────────────────────────────────────

export function getItem(key: string): string | null {
  if (!isBrowser()) return null;
  try {
    return globalThis.localStorage.getItem(key);
  } catch (err) {
    logger.warn(`safeStorage.getItem(${key}) failed`, err);
    return null;
  }
}

export function setItem(key: string, value: string): void {
  if (!isBrowser()) return;
  try {
    globalThis.localStorage.setItem(key, value);
  } catch (err) {
    logger.warn(`safeStorage.setItem(${key}) failed`, err);
  }
}

export function removeItem(key: string): void {
  if (!isBrowser()) return;
  try {
    globalThis.localStorage.removeItem(key);
  } catch (err) {
    logger.warn(`safeStorage.removeItem(${key}) failed`, err);
  }
}

// ─── JSON values ──────────────────────────────────────────────────────────────

export function getJson<T>(key: string, fallback: T): T {
  const raw = getItem(key);
  if (raw == null) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch (err) {
    logger.warn(`safeStorage.getJson(${key}) parse failed`, err);
    return fallback;
  }
}

export function setJson<T>(key: string, value: T): void {
  try {
    setItem(key, JSON.stringify(value));
  } catch (err) {
    logger.warn(`safeStorage.setJson(${key}) stringify failed`, err);
  }
}

/**
 * Bulk-clear `localStorage`. Rarely needed in app code (prefer removing the
 * specific `STORAGE_KEYS` entry you own) — the main legitimate callers are
 * the logout scrub and test setup that wants a clean slate.
 */
export function clear(): void {
  if (!isBrowser()) return;
  try {
    globalThis.localStorage.clear();
  } catch (err) {
    logger.warn("safeStorage.clear failed", err);
  }
}

// ─── sessionStorage parity ────────────────────────────────────────────────────

export function getSessionItem(key: string): string | null {
  if (!isBrowser()) return null;
  try {
    return globalThis.sessionStorage.getItem(key);
  } catch (err) {
    logger.warn(`safeStorage.getSessionItem(${key}) failed`, err);
    return null;
  }
}

export function setSessionItem(key: string, value: string): void {
  if (!isBrowser()) return;
  try {
    globalThis.sessionStorage.setItem(key, value);
  } catch (err) {
    logger.warn(`safeStorage.setSessionItem(${key}) failed`, err);
  }
}

export function removeSessionItem(key: string): void {
  if (!isBrowser()) return;
  try {
    globalThis.sessionStorage.removeItem(key);
  } catch (err) {
    logger.warn(`safeStorage.removeSessionItem(${key}) failed`, err);
  }
}

/**
 * Bulk-clear `sessionStorage`. The only legitimate caller today is the
 * auth logout flow, which scrubs every per-tab cache to make sure no
 * PHI bleeds across user sessions sharing the same browser tab.
 */
export function clearSession(): void {
  if (!isBrowser()) return;
  try {
    globalThis.sessionStorage.clear();
  } catch (err) {
    logger.warn("safeStorage.clearSession failed", err);
  }
}

/** Default-export bag so you can `import safeStorage from …` if preferred. */
export const safeStorage = {
  getItem,
  setItem,
  removeItem,
  getJson,
  setJson,
  clear,
  getSessionItem,
  setSessionItem,
  removeSessionItem,
  clearSession,
} as const;

export default safeStorage;
